import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { normalizeVisibleColumnIds } from './utils/normalizeVisibleColumnIds';
import {
    readPersistedColumnVisibilityState,
    writePersistedColumnVisibilityState,
} from './utils/persistGridColumnVisibility';
import type {
    GridColumnVisibilityContextValue,
    GridColumnVisibilityDefinition,
    GridColumnVisibilityProviderProps,
    GridColumnVisibilityStoreValue,
} from './types';

const GridColumnVisibilityStoreContext = createContext<GridColumnVisibilityStoreValue | null>(null);

function toPublicContextValue(store: GridColumnVisibilityStoreValue): GridColumnVisibilityContextValue {
    const { registerColumnDefinitions: _register, ...publicFields } = store;

    return publicFields;
}

/**
 * Провайдер видимости колонок грида: хранит описание колонок и текущий набор видимых,
 * синхронизирует состояние с persistentStorage по `storageKey`.
 *
 * Описание колонок и значения по умолчанию задаются из хука грида через
 * {@link useRegisterGridColumnVisibilityColumnDefinitions}.
 * @kind component
 */
export function GridColumnVisibilityProvider({
    storageKey,
    settingsTitle,
    children,
}: GridColumnVisibilityProviderProps) {
    const [columnDefinitions, setColumnDefinitions] = useState<GridColumnVisibilityContextValue['columnDefinitions']>(
        []
    );
    const [visibleColumnIds, setVisibleColumnIdsState] = useState<string[]>([]);

    const [persistedSnapshot] = useState(() => readPersistedColumnVisibilityState(storageKey));

    const hasCompletedInitialRegistrationRef = useRef(false);
    const seenColumnIdsRef = useRef<Set<string>>(new Set(persistedSnapshot?.seenColumnIds ?? []));

    const markColumnsAsSeen = useCallback((nextColumnDefinitions: readonly GridColumnVisibilityDefinition[]) => {
        for (const definition of nextColumnDefinitions) {
            seenColumnIdsRef.current.add(definition.columnId);
        }
    }, []);

    const registerColumnDefinitions = useCallback(
        (
            nextColumnDefinitions: GridColumnVisibilityContextValue['columnDefinitions'],
            defaultVisibleColumnIds: readonly string[]
        ) => {
            setColumnDefinitions(nextColumnDefinitions);

            if (nextColumnDefinitions.length === 0) {
                return;
            }

            if (!hasCompletedInitialRegistrationRef.current) {
                hasCompletedInitialRegistrationRef.current = true;

                const hasPersistedSnapshot = persistedSnapshot !== null;
                const sourceColumnIds = hasPersistedSnapshot
                    ? persistedSnapshot.visibleColumnIds
                    : [...defaultVisibleColumnIds];

                const defaultVisibleColumnIdsToMergeIn = hasPersistedSnapshot
                    ? defaultVisibleColumnIds.filter((columnId) => !seenColumnIdsRef.current.has(columnId))
                    : undefined;

                markColumnsAsSeen(nextColumnDefinitions);

                setVisibleColumnIdsState(
                    normalizeVisibleColumnIds(sourceColumnIds, nextColumnDefinitions, defaultVisibleColumnIdsToMergeIn)
                );

                return;
            }

            markColumnsAsSeen(nextColumnDefinitions);

            setVisibleColumnIdsState((previousVisibleColumnIds) =>
                normalizeVisibleColumnIds(previousVisibleColumnIds, nextColumnDefinitions)
            );
        },
        [markColumnsAsSeen, persistedSnapshot]
    );

    const setVisibleColumnIds = useCallback(
        (nextVisibleColumnIds: string[]) => {
            setVisibleColumnIdsState(normalizeVisibleColumnIds(nextVisibleColumnIds, columnDefinitions));
        },
        [columnDefinitions]
    );

    const toggleColumnVisibility = useCallback(
        (columnId: string, isVisible: boolean) => {
            const columnDefinition = columnDefinitions.find((definition) => definition.columnId === columnId);

            if (!columnDefinition || columnDefinition.isRequired) {
                return;
            }

            setVisibleColumnIdsState((previousVisibleColumnIds) => {
                const nextVisibleColumnIds = isVisible
                    ? [...previousVisibleColumnIds, columnId]
                    : previousVisibleColumnIds.filter((existingColumnId) => existingColumnId !== columnId);

                return normalizeVisibleColumnIds(nextVisibleColumnIds, columnDefinitions);
            });
        },
        [columnDefinitions]
    );

    const isColumnVisible = useCallback(
        (columnId: string) => {
            const columnDefinition = columnDefinitions.find((definition) => definition.columnId === columnId);

            if (columnDefinition?.isAvailable && !columnDefinition.isAvailable()) {
                return false;
            }

            if (columnDefinition?.isRequired) {
                return true;
            }

            return visibleColumnIds.includes(columnId);
        },
        [columnDefinitions, visibleColumnIds]
    );

    useEffect(() => {
        if (!hasCompletedInitialRegistrationRef.current) {
            return;
        }

        writePersistedColumnVisibilityState(storageKey, {
            visibleColumnIds,
            seenColumnIds: [...seenColumnIdsRef.current],
        });
    }, [storageKey, visibleColumnIds]);

    const storeValue = useMemo<GridColumnVisibilityStoreValue>(() => {
        return {
            columnDefinitions,
            visibleColumnIds,
            settingsTitle,
            setVisibleColumnIds,
            toggleColumnVisibility,
            isColumnVisible,
            registerColumnDefinitions,
        };
    }, [
        columnDefinitions,
        isColumnVisible,
        registerColumnDefinitions,
        setVisibleColumnIds,
        settingsTitle,
        toggleColumnVisibility,
        visibleColumnIds,
    ]);

    return (
        <GridColumnVisibilityStoreContext.Provider value={storeValue}>
            {children}
        </GridColumnVisibilityStoreContext.Provider>
    );
}

/**
 * Доступ к настройкам видимости колонок (модалки, шапка и т.п.).
 * Требует обёртки в {@link GridColumnVisibilityProvider}.
 * @kind helper
 * @returns настройки видимости колонок
 */
export function useGridColumnVisibility(): GridColumnVisibilityContextValue | null {
    const store = useContext(GridColumnVisibilityStoreContext);

    if (!store) {
        return null;
    }

    return toPublicContextValue(store);
}

type RegisterGridColumnVisibilityColumnDefinitionsOptions = {
    columnDefinitions: readonly GridColumnVisibilityDefinition[];
    defaultVisibleColumnIds: readonly string[];
};

/**
 * Регистрирует описание колонок грида в провайдере и применяет сохранённое состояние
 * или `defaultVisibleColumnIds`. Вызывайте из хука сборки колонок (например, рядом с `useMemo` колонок).
 * @kind component
 */
export function useRegisterGridColumnVisibilityColumnDefinitions({
    columnDefinitions,
    defaultVisibleColumnIds,
}: RegisterGridColumnVisibilityColumnDefinitionsOptions): void {
    const registerColumnDefinitions = useContext(GridColumnVisibilityStoreContext)?.registerColumnDefinitions;

    useLayoutEffect(() => {
        registerColumnDefinitions?.([...columnDefinitions], [...defaultVisibleColumnIds]);
    }, [registerColumnDefinitions, columnDefinitions, defaultVisibleColumnIds]);
}
